/**
 * API Service Manager
 *
 * The app's API surface for everything that isn't a plain read migrated to
 * TanStack Query: credential storage/validation, snapshots, assignment
 * notes, notification preferences, and default-user updates. Course reads
 * (courses/courseDetails/courseHistory) are still reached through this
 * manager's getCourses/getCourseDetails/getCourseHistory methods, called
 * from canvasService, called from inside a TanStack `useQuery`/
 * `fetchQuery` in CanvasDataContext -- the caching and invalidation live at
 * that layer now, not here.
 *
 * Task 4.4b: this used to wrap every method in an enhanced,
 * in-memory-cache-plus-circuit-breaker branch (selected by
 * `apiConfig.caching.enabled`, true in production only) with a
 * `legacyApi.get(...)` fallback for when that flag was off. The enhanced
 * service that branch called into has been deleted (its cache is
 * superseded by TanStack Query for the reads that moved there; its circuit
 * breaker had no reads left to guard once the enhanced branch was gone --
 * see docs/IMPLEMENTATION_PLAN.md task 4.4b). Every method below now
 * always takes what used to be the "legacy" branch; apiServiceManager
 * itself no longer caches anything.
 */

import legacyApi from './api'; // Your existing API service

/**
 * Reads one property off a caught value.
 *
 * `catch` binds `unknown` under `strict`, and the fields these call sites
 * want (`response.status`, `config.url`) are axios's, not `Error`'s -- so
 * `instanceof Error` would not reach them. This checks that there is a
 * non-null object before indexing it, which makes the `Record<string,
 * unknown>` view true rather than merely accepted: every property of a
 * non-null object is readable, and `unknown` is an honest type for the
 * result because nothing here has verified what the property holds.
 *
 * The returned values are only ever logged, so they are never narrowed
 * further.
 */
const readProp = (value: unknown, key: string): unknown =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)[key]
    : undefined;

interface Credentials {
  token: string;
  userId: string;
  rootUrl: string;
}

class ApiServiceManager {
  /**
   * Courses API
   */
  async getCourses(credentials: Credentials): Promise<any> {
    return legacyApi.get('/courses', {
      headers: this.buildHeaders(credentials)
    });
  }

  /**
   * Course Details API
   */
  async getCourseDetails(courseId: any, credentials: any): Promise<any> {
    return legacyApi.get(`/courses/${courseId}/details`, {
      headers: this.buildHeaders(credentials)
    });
  }

  /**
   * Course History API
   */
  async getCourseHistory(courseId: any, credentials: any): Promise<any> {
    return legacyApi.get(`/courses/${courseId}/history`, {
      headers: this.buildHeaders(credentials)
    });
  }

  /**
   * Credentials API
   */
  async storeCredentials(credentials: any): Promise<any> {
    return legacyApi.post('/credentials', credentials);
  }

  async getCredentials(): Promise<any> {
    // Debug credentials call in development only
    if (import.meta.env.DEV) {
      console.log('🔑 Guest mode:', sessionStorage.getItem('isGuestMode') === 'true');
    }

    try {
      const result = await legacyApi.get('/credentials');
      return result;
    } catch (error) {
      const response = readProp(error, 'response');
      const config = readProp(error, 'config');
      // Only log detailed errors for debugging the 403 issue
      console.error('❌ Credentials API Error:', {
        status: readProp(response, 'status'),
        statusText: readProp(response, 'statusText'),
        url: readProp(config, 'url'),
        method: readProp(config, 'method')
      });
      throw error;
    }
  }

  /**
   * Snapshots API. Reached only through canvasService's fetchSnapshots/
   * storeSnapshot/deleteSnapshot, which have no importers anywhere outside
   * canvasService's own default-export object (confirmed dead in task
   * 4.4b's survey; the live snapshot path is services/snapshotService.js,
   * which talks to api.ts directly). Left in place -- not this task's call
   * to make, flagged as a follow-up -- so these keep the same call
   * signature canvasService already uses; `userId` is unused here now that
   * the cache-tag invalidation that consumed it is gone.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getSnapshots(userId: any): Promise<any> {
    return legacyApi.get('/snapshots');
  }

  async storeSnapshot(snapshotData: any): Promise<any> {
    return legacyApi.post('/snapshots', snapshotData);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async deleteSnapshot(snapshotId: any, userId: any): Promise<any> {
    return legacyApi.delete(`/snapshots/${snapshotId}`);
  }

  /**
   * Assignment Notes API - Simplified for one note per assignment
   */
  async getAssignmentNotes(userId: any): Promise<any> {
    return legacyApi.get(`/assignment-notes?userId=${userId}`);
  }

  async saveAssignmentNote(noteData: any): Promise<any> {
    // POST handles both create and update since there's only one note per assignment
    return legacyApi.post('/assignment-notes', noteData);
  }

  async deleteAssignmentNote(courseId: any, assignmentId: any): Promise<any> {
    return legacyApi.delete(`/assignment-notes/${courseId}/${assignmentId}`);
  }

  /**
   * Token validation
   */
  async validateToken(token: any, userId: any, rootUrl: any): Promise<any> {
    return legacyApi.post('/validate-token', {
      token,
      userId,
      rootUrl
    });
  }

  /**
   * Update default Canvas user preference
   */
  async updateDefaultUser(data: any): Promise<any> {
    return legacyApi.post('/update-default-user', data);
  }

  /**
   * Notification preferences (task 3.5): digest frequency, grade-drop and
   * missing-assignment thresholds, and per-child toggles. Never cached -
   * this is settings data the user expects to see reflect their latest
   * save immediately.
   */
  async getNotificationPreferences(): Promise<any> {
    return legacyApi.get('/preferences');
  }

  async saveNotificationPreferences(data: any): Promise<any> {
    return legacyApi.put('/preferences', data);
  }

  /**
   * Utility methods
   */
  buildHeaders(credentials: any): any {
    const headers: any = {};

    if (credentials) {
      if (credentials.token) headers['X-Canvas-Token'] = credentials.token;
      if (credentials.userId) headers['X-Canvas-User-Id'] = credentials.userId;
      if (credentials.rootUrl) headers['X-Canvas-Root-Url'] = credentials.rootUrl;
    }

    return headers;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<any> {
    try {
      const startTime = performance.now();
      await legacyApi.get('/health');
      const duration = performance.now() - startTime;

      return {
        status: 'healthy',
        responseTime: duration,
        enhanced: false,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        status: 'unhealthy',
        error: (error as Error).message,
        enhanced: false,
        timestamp: new Date().toISOString()
      };
    }
  }
}

// Create singleton instance
const apiServiceManager = new ApiServiceManager();

export default apiServiceManager;
